
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

interface DragState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface Rectangle {
  left: number;
  top: number;
  width: number;
  height: number;
}

@Component
export default class DragSelection extends Vue {
  @Prop({ required: true })
  readonly photos!: any[];

  @Prop({ required: true })
  readonly photoRefs!: { [key: string]: Vue };

  private isDragging = false;
  private pendingDragStart = false;
  private readonly dragStartThreshold = 6;
  private dragState: DragState = {
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0
  };

  get rectangleStyle(): any {
    if (!this.isDragging) return {};

    const container = this.$refs.dragContainer as HTMLElement;
    if (!container) return {};

    const rect = container.getBoundingClientRect();
    
    // Convert container-relative coordinates to viewport coordinates for fixed positioning
    const startX = this.dragState.startX + rect.left;
    const startY = this.dragState.startY + rect.top;
    const currentX = this.dragState.currentX + rect.left;
    const currentY = this.dragState.currentY + rect.top;

    const left = Math.min(startX, currentX);
    const top = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  }

  private startDrag(event: MouseEvent): void {
    // Disable drag selection on mobile/touch devices
    if (this.isMobileDevice()) {
      return;
    }

    // Only start drag if clicking in empty space, not on interactive elements
    const target = event.target as HTMLElement;
    
    // Check if the click is on an interactive element that should NOT trigger drag
    const isOnInteractiveElement = target.closest('.v-checkbox, .v-btn, .v-tooltip, .photoBought, .photoLoved, .v-img, .conflict-instruction, .finalization-instruction, .lightbox, .v-text-field, .v-input, input, textarea, [contenteditable]');
    
    // Additional check for input elements directly
    const isInputElement = target.tagName && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName.toUpperCase());
    const isContentEditable = target.isContentEditable;
    
    // Check if the click is in areas where drag should be disabled (header, navigation, overlays)
    const isInExcludedArea = target.closest('.v-navigation-drawer, .v-app-bar, .v-footer, .v-overlay, .v-menu__content, .v-dialog, .v-snack');
    
    // Don't start drag if clicking on interactive elements or excluded areas
    if (isOnInteractiveElement || isInExcludedArea || isInputElement || isContentEditable) {
      return;
    }
    
    // At this point, allow dragging from anywhere else (very permissive approach)
    
    // For sessions with photos, allow dragging from anywhere in the container area
    const container = this.$refs.dragContainer as HTMLElement;
    
    // If we have photos, be very permissive about where dragging can start
    if (this.photos.length > 0) {
      // Check if we're somewhere in the justified container area
      const isInContainer = container && container.contains(target);
      const isInJustifiedLayout = target.closest('.justified-layout');
      
      // Allow dragging from container area or any area that could be the session content
      if (!isInContainer && !isInJustifiedLayout) {
        // Additional check for session content areas
        const isInSessionArea = target.closest('.v-main, .fluid');
        if (!isInSessionArea) {
          return;
        }
      }
    } else {
      // For sessions without photos, only allow dragging from the container itself
      const isInContainer = container && container.contains(target);
      if (!isInContainer) {
        return;
      }
    }

    // Use container coordinates for positioning the rectangle
    const rect = container.getBoundingClientRect();
    
    // Calculate start position relative to container
    let startY = event.clientY - rect.top;
    
    // Allow negative Y coordinates (dragging from above the container)
    // No restrictions - let users drag from anywhere
    
    this.dragState.startX = event.clientX - rect.left;
    this.dragState.startY = startY;
    this.dragState.currentX = this.dragState.startX;
    this.dragState.currentY = startY;
    this.pendingDragStart = true;
  }

  private updateDrag(event: MouseEvent): void {
    if (!this.pendingDragStart && !this.isDragging) return;

    const rect = (this.$refs.dragContainer as HTMLElement).getBoundingClientRect();
    const currentX = event.clientX - rect.left;
    const currentY = event.clientY - rect.top;

    if (!this.isDragging) {
      const deltaX = currentX - this.dragState.startX;
      const deltaY = currentY - this.dragState.startY;
      const distance = Math.hypot(deltaX, deltaY);

      if (distance < this.dragStartThreshold) {
        return;
      }

      this.isDragging = true;
      event.preventDefault();
    }

    this.dragState.currentX = currentX;
    this.dragState.currentY = currentY;

    // Check for intersections with photos
    this.checkPhotoIntersections();
  }

  private endDrag(event: MouseEvent): void {
    if (!this.pendingDragStart && !this.isDragging) return;

    this.pendingDragStart = false;
    if (!this.isDragging) return;

    this.isDragging = false;
    
    // Final intersection check
    this.checkPhotoIntersections();
    
    // Reset drag selection state in parent component
    this.$emit('dragEnded');
  }

  private checkPhotoIntersections(): void {
    const selectionRect = this.getSelectionRectangle();
    const photosInRectangle: any[] = [];
    const container = this.$refs.dragContainer as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const photoElements = Array.from(
      container.querySelectorAll<HTMLElement>('.photoItem')
    );

    this.photos.forEach((photo, index) => {
      const photoElement = photoElements[index];
      if (!photoElement) return;

      const photoRect = photoElement.getBoundingClientRect();

      // Convert photo rect to container-relative coordinates
      const relativePhotoRect = {
        left: photoRect.left - containerRect.left,
        top: photoRect.top - containerRect.top,
        right: photoRect.right - containerRect.left,
        bottom: photoRect.bottom - containerRect.top,
      };

      // Check if rectangles intersect
      if (this.rectanglesIntersect(selectionRect, relativePhotoRect)) {
        photosInRectangle.push({ photo, index });
      }
    });

    // Emit the current photos in rectangle (not additive - this replaces the selection)
    this.$emit('photosInRectangle', photosInRectangle);
  }

  private getSelectionRectangle(): Rectangle {
    const left = Math.min(this.dragState.startX, this.dragState.currentX);
    const top = Math.min(this.dragState.startY, this.dragState.currentY);
    const width = Math.abs(this.dragState.currentX - this.dragState.startX);
    const height = Math.abs(this.dragState.currentY - this.dragState.startY);

    return { left, top, width, height };
  }

  private rectanglesIntersect(rect1: Rectangle, rect2: any): boolean {
    return !(
      rect1.left > rect2.right ||
      rect2.left > rect1.left + rect1.width ||
      rect1.top > rect2.bottom ||
      rect2.top > rect1.top + rect1.height
    );
  }

  private isMobileDevice(): boolean {
    // Check for touch support
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Check user agent for mobile devices
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone', 'mobile'];
    const isMobileUA = mobileKeywords.some(keyword => userAgent.includes(keyword));
    
    // Check screen size (mobile-like dimensions)
    const isSmallScreen = window.innerWidth <= 768;
    
    // Consider it mobile if it has touch AND (mobile UA OR small screen)
    return hasTouch && (isMobileUA || isSmallScreen);
  }

  mounted() {
    // Add document-level event listeners to capture drag events from anywhere on the page
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
    document.addEventListener('mousemove', this.handleDocumentMouseMove);
    document.addEventListener('mouseup', this.handleDocumentMouseUp);
  }

  beforeDestroy() {
    // Clean up event listeners
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    document.removeEventListener('mousemove', this.handleDocumentMouseMove);
    document.removeEventListener('mouseup', this.handleDocumentMouseUp);
  }

  private handleDocumentMouseDown = (event: MouseEvent) => {
    this.startDrag(event);
  }

  private handleDocumentMouseMove = (event: MouseEvent) => {
    this.updateDrag(event);
  }

  private handleDocumentMouseUp = (event: MouseEvent) => {
    this.endDrag(event);
  }
}
